import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';

// Renders the role name / tagline via the 'roles' i18n namespace for seeded
// roles (translation_key set), or straight from the DB for admin-created ones.
const roleName = (t, role) =>
  role.translation_key
    ? t(`${role.translation_key}.name`, { ns: 'roles', defaultValue: role.name })
    : role.name;

const roleTagline = (t, role) =>
  role.translation_key
    ? t(`${role.translation_key}.tagline`, { ns: 'roles', defaultValue: role.tagline || '' })
    : (role.tagline || '');

const RolePickerModal = ({ teamId, onClose, onChanged }) => {
  const { t } = useTranslation(['common', 'roles']);
  const [roles, setRoles] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () =>
    apiRequest('GET', `/team-roles/${teamId}`)
      .then((res) => setRoles(res.roles || []))
      .catch(() => setRoles([]));

  useEffect(() => {
    load();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const mine = roles?.find((r) => r.taken_by?.is_self) || null;

  const setRole = async (roleId) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiRequest('POST', '/set-own-role', { role_id: roleId });
      onChanged?.();
      onClose();
    } catch (e) {
      // Someone grabbed it first — surface a friendly note and refresh the
      // taken-state so the loser sees who holds it now.
      setError(e.message === 'roleTaken' ? 'roleTaken' : 'somethingWentWrong');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-roles-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="pt-roles-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="pt-roles-head">
          <div>
            <h3 className="pt-roles-title">{t('pickYourRole', { defaultValue: 'Pick your role' })}</h3>
            <p className="pt-roles-hint">{t('pickRoleHint', { defaultValue: 'First come, first served!' })}</p>
          </div>
          <button type="button" className="pt-roles-close" onClick={onClose}
                  aria-label={t('close', { defaultValue: 'Close' })}>
            <Icon name="cross" />
          </button>
        </header>

        {error && <div className="pt-roles-error">{t(error, { defaultValue: 'Something went wrong' })}</div>}

        {roles === null ? (
          <div className="pt-roles-loading">{t('loading', { defaultValue: 'Loading…' })}</div>
        ) : roles.length === 0 ? (
          <div className="pt-roles-loading">{t('noRolesYet', { defaultValue: 'No roles to pick yet.' })}</div>
        ) : (
          <div className="pt-roles-grid">
            {roles.map((r) => {
              const takenByOther = r.taken_by && !r.taken_by.is_self;
              const isMine = r.taken_by?.is_self;
              const color = r.color || '#64748B';
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={busy || takenByOther}
                  onClick={() => setRole(r.id)}
                  className={`pt-role-card${isMine ? ' is-mine' : ''}${takenByOther ? ' is-taken' : ''}`}
                  style={{ borderColor: color, backgroundColor: `${color}14` }}
                >
                  <span className="pt-role-emoji">{r.emoji || '⭐'}</span>
                  <span className="pt-role-name" style={{ color }}>{roleName(t, r)}</span>
                  <span className="pt-role-tagline">{roleTagline(t, r)}</span>
                  {isMine && (
                    <span className="pt-role-flag pt-role-flag--mine">
                      <Icon name="check" /> {t('yourTeamRole', { defaultValue: 'Your role' })}
                    </span>
                  )}
                  {takenByOther && (
                    <span className="pt-role-flag pt-role-flag--taken">
                      {t('takenBy', { name: r.taken_by.full_name, defaultValue: 'Taken' })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {mine && (
          <footer className="pt-roles-foot">
            <button type="button" className="pt-roles-clear" disabled={busy} onClick={() => setRole(null)}>
              <Icon name="cross" /> {t('clearRole', { defaultValue: 'Clear my role' })}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default RolePickerModal;
